import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '../../ui/core';
import api from '../../lib/api';
import { REASON_MAX, validateReason } from './sdpFormatters';

// POST /reset writes one manual sdp_policy_change; the nightly job consumes it
// as a hard reset (affects the chosen city, or global).
const ResetDialog = ({ open, cities = [], onClose, onReset }) => {
  const [reason, setReason] = useState('');
  const [cityId, setCityId] = useState('');
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Which opening a response belongs to: a late response from an earlier
  // opening must not close (or error) a reopened dialog.
  const opening = useRef(0);

  useEffect(() => {
    opening.current += 1;
    if (open) {
      setReason('');
      setCityId('');
      setError(null);
      setServerError(null);
      setSubmitting(false);
    }
  }, [open]);

  const cityName = (cities.find((c) => c.cityId === cityId) || {}).cityName;

  const onConfirm = async () => {
    const reasonError = validateReason(reason);
    setError(reasonError);
    if (reasonError) return;
    const sentFor = opening.current;
    setSubmitting(true);
    setServerError(null);
    try {
      const entry = await api.supplyDemandPace.reset({ reason: reason.trim(), cityId: cityId || undefined });
      // The reset was written either way: let the caller refresh.
      onReset && (await onReset(entry));
      if (sentFor === opening.current) onClose();
    } catch (e) {
      if (sentFor === opening.current) setServerError(e.message);
    } finally {
      if (sentFor === opening.current) setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reset the SDP reference</DialogTitle>
      <DialogContent>
        <Stack spacing="12px" sx={{ pt: '4px' }}>
          <Alert severity="warning">
            {`This writes a manual policy change. The nightly job treats it as a hard reset: the refit window restarts for ${
              cityId ? cityName || cityId : 'every city (global)'
            } and the next reference carries this reset reason, which also clears safe mode anywhere it's still open.`}
          </Alert>
          <TextField
            select
            name="resetCityId"
            label="City (optional)"
            size="small"
            value={cityId}
            onChange={(e) => setCityId(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            <option value="">global</option>
            {cities.map((c) => (
              <option key={c.cityId} value={c.cityId}>
                {c.cityName || c.cityId}
              </option>
            ))}
          </TextField>
          <TextField
            name="resetReason"
            label="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={!!error}
            helperText={error || `${reason.trim().length}/${REASON_MAX}`}
            multiline
            minRows={2}
            fullWidth
          />
          {serverError && <Alert severity="error">{serverError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={submitting} disableElevation>
          Write reset
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetDialog;
